import { ListItem, ListView } from '@dev-spendesk/grapes';
import React from 'react';
import { useDispatch } from 'react-redux';

import { ErrorBoundary } from 'common/components/withErrorBoundary';
import { useFeature } from 'common/hooks/useFeature';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import type { AppDispatch } from 'modules/app/redux/store';
import { fetchWireTransferActivationStatus } from 'modules/company/billing-legacy/redux';
import FEATURES from 'src/core/constants/features';

import { useShouldDisplayWireTransferPaymentMethod } from './useShouldDisplayWireTransferPaymentMethod';
import { WireTransfer } from '../../../WireTransfer';
import { getAllowedSpendingMethods } from '../../spendingMethods';
import { SpendingMethod } from '../SpendingMethod';

const COUNTRY_MILEAGE_AUTO_SETUP = ['DE', 'FR', 'GB'];

export const ListSpendingMethods = () => {
  const { t } = useTranslation('global');
  const company = useCompany();
  const hasMileageScheme =
    !!company.mileage_scheme ||
    COUNTRY_MILEAGE_AUTO_SETUP.includes(company.country);

  const dispatch = useDispatch<AppDispatch>();
  const isInvoiceEnabled = useFeature(FEATURES.SUBMIT_MY_INVOICE);
  const isCostCenterActivated = useFeature(FEATURES.COST_CENTERS_ACTIVATED);
  const isExpenseClaimsEnabled = useFeature(FEATURES.EXPENSE_CLAIMS);

  const isPurchaseOrderEnabled = useFeature(FEATURES.PURCHASE_ORDERS_ACTIVATED);
  const isControlOnPurchaseOrderEnabled = useFeature(
    FEATURES.CONTROL_ON_PURCHASE_ORDER,
  );
  const isControlOnSubmitMyInvoice = useFeature(
    FEATURES.CONTROL_ON_SUBMIT_MY_INVOICE,
  );

  const isDeliveryNoteEnabled = useFeature(FEATURES.DELIVERY_NOTES_FEATURE);

  const getShouldDisplayWireTransferPaymentMethod =
    useShouldDisplayWireTransferPaymentMethod();

  React.useEffect(() => {
    dispatch(fetchWireTransferActivationStatus());
  }, [isExpenseClaimsEnabled]);

  // Memoized spending methods to avoid infinite loop
  const allowedSpendingMethods = React.useMemo(() => {
    return getAllowedSpendingMethods(company.country, {
      isControlOnSubmitMyInvoice,
      isInvoiceEnabled,
      isControlOnPurchaseOrderEnabled,
      isPurchaseOrderEnabled,
      isDeliveryNoteEnabled,
      isCostCenterActivated,
      hasMileageScheme,
    });
  }, [
    isControlOnSubmitMyInvoice,
    isInvoiceEnabled,
    isPurchaseOrderEnabled,
    isDeliveryNoteEnabled,
    isControlOnPurchaseOrderEnabled,
    isCostCenterActivated,
    hasMileageScheme,
  ]);

  return (
    <ErrorBoundary context={{ scope: 'list-spending-methods', team: 'kyc' }}>
      <section aria-labelledby="paymentsSettings" className="pt-24">
        <h1 id="paymentsSettings" className="text-content-primary heading-l">
          {t('payments.settings.title')}
        </h1>
        <p className="text-content-secondary-bg-primary body-m mt-8">
          {t('payments.settings.description')}
        </p>
        <ListView className="mt-24">
          {allowedSpendingMethods.map(({ featureCode, isDisabled }) => (
            <ListItem key={featureCode}>
              <SpendingMethod
                featureCode={featureCode}
                isDisabled={isDisabled}
              />
            </ListItem>
          ))}
          {getShouldDisplayWireTransferPaymentMethod('expenses') && (
            <ListItem>
              <WireTransfer wireTransferPayableEntity="expenses" />
            </ListItem>
          )}
          {getShouldDisplayWireTransferPaymentMethod('invoices') && (
            <ListItem>
              <WireTransfer wireTransferPayableEntity="invoices" />
            </ListItem>
          )}
        </ListView>
      </section>
    </ErrorBoundary>
  );
};
